import { useCallback } from 'react'
import { handleEmailAdd } from '@/utils/email-connect'
import { getEmailConnectionAuthFailure } from '@/utils/get-user-error-message'
import { showSnackbar, SnackbarType } from '@/utils/snackbar'

const reconnectMessage = (senderEmail: string): string =>
    senderEmail
        ? `Your email connection for ${senderEmail} has expired. Reconnect it to continue sending emails.`
        : 'Your email connection has expired. Reconnect it to continue sending emails.'

const reconnectSubtitle = (senderEmail: string): string =>
    senderEmail
        ? `Reconnect ${senderEmail} to continue sending emails and receiving replies.`
        : 'Reconnect your email to continue sending emails and receiving replies.'

export const useEmailReconnectOnAuthError = (verifyAuth: () => Promise<void>) => {
    return useCallback(
        (error: unknown, senderEmail: string): string | null => {
            const authFailure = getEmailConnectionAuthFailure(error)
            if (!authFailure) return null

            const message = reconnectMessage(senderEmail)

            void handleEmailAdd(
                () => {
                    void verifyAuth()
                        .then(() => showSnackbar(SnackbarType.SUCCESS, 'Email account reconnected successfully'))
                        .catch(() => showSnackbar(SnackbarType.ERROR, 'Email reconnected, but the account could not be refreshed. Please reload the page.'))
                },
                (connectionError) => {
                    if (connectionError) showSnackbar(SnackbarType.ERROR, connectionError)
                },
                undefined,
                {
                    title: 'Reconnect Email',
                    subtitle: reconnectSubtitle(senderEmail),
                    errorMessage: authFailure.message ?? message,
                    ...(senderEmail ? { loginHint: senderEmail } : {}),
                }
            )

            return message
        },
        [verifyAuth]
    )
}
